import React, { useContext, useEffect, useState } from "react";
import { DispatchContext, StateContext } from "bodycodi/cmap/app/components/common/context";

// components
import AgreePopup from "./AgreePopup";
import ObjectionPopup from "./ObjectionPopup";
import ContractDetailPop from "./contract";
import EachSalaryDetailPop from "./each";

// assets
import ReceiptIcon from "bodycodi/cmap/app/svg/receipt-icon.svg";
import RightIcon from "bodycodi/cmap/app/svg/icon_right_gray_18.svg";

// utils
import {
  changeDateFormat,
  getDwk,
  getPayrollStatus,
  moneyFormatter,
} from "bodycodi/cmap/app/js/common";
import { api } from "bodycodi/cmap/app/js/api";

const SalaryDetails = ({ seqNewSettlementPayroll, setRefetch }: any) => {
  const { dispatch } = useContext(DispatchContext);
  const { state } = useContext(StateContext);
  const [salaryType, setSalaryType] = useState<"SALES" | "APPOINTMENT" | "GROUP">("SALES");
  const [detail, setDetail] = useState<any>({});

  const handleOpenDrawer = (type: "agree" | "objection") => {
    if (type === "agree") {
      dispatch({
        type: "POP_UP_OPEN",
        id: "SALARY_AGREE_POP",
        open: { salaryAgreePop: true },
      });
    } else {
      dispatch({
        type: "POP_UP_OPEN",
        id: "SALARY_OBJECTION_POP",
        open: { salaryObjectionPop: true },
      });
    }
  };

  const openEachSalaryDetail = (type: any) => {
    setSalaryType(type);
    dispatch({
      type: "POP_UP_OPEN",
      id: "EACH_SALARY_DETAIL_POP",
      open: { eachSalaryDetailPop: true },
    });
  };

  const openContractDetails = () => {
    dispatch({
      type: "POP_UP_OPEN",
      id: "CONTRACT_DETAIL_POP",
      open: { contractDetailPop: true },
    });
  };

  const closeAfterAction = () => {
    dispatch({
      type: "POP_UP_OPEN",
      id: "SALARY_DETAIL_POP",
      open: { salaryDetailPop: false },
    });
    setRefetch((prev: any) => prev + 1);
  };

  const fetchPayrollDetail = async () => {
    try {
      dispatch({ type: "LOADING_START" });
      await api.get(`/payroll/settlement/payrolls/${seqNewSettlementPayroll}`).then(({ data }) => {
        setDetail(data);
        dispatch({ type: "LOADING_END" });
      });
    } catch (error) {
      dispatch({ type: "LOADING_END" });
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPayrollDetail();
  }, []);

  if (!state.loading && Object.keys(detail).length === 0)
    return (
      <div className="popup-body no-padding salary-details no-data">
        <div>급여 상세 내역이 없습니다.</div>
      </div>
    );

  return (
    <div className="popup-body no-padding salary-details">
      <Overview onClick={openContractDetails} detail={detail} />

      <Section withRightArrow={false} title="기본급" price={detail?.settlementBasePay} />

      <Section
        title="영업 커미션"
        onClick={() => openEachSalaryDetail("SALES")}
        price={detail?.salesCommission}
        submodules={[
          { id: 1, title: "개인 커미션", rightText: moneyFormatter(detail?.personalCommission) },
          { id: 2, title: "팀 커미션", rightText: moneyFormatter(detail?.teamCommission) },
          { id: 3, title: "센터 커미션", rightText: moneyFormatter(detail?.centerCommission) },
          { id: 4, title: "조정액", rightText: moneyFormatter(detail?.commissionAdjustment) },
        ]}
      />

      <Section
        title="개인레슨 수당"
        onClick={() => openEachSalaryDetail("APPOINTMENT")}
        price={detail?.totalAppointmentAllowance}
        submodules={[
          { id: 1, title: "수당", rightText: moneyFormatter(detail?.appointmentAllowance) },
          {
            id: 2,
            title: "조정액",
            rightText: moneyFormatter(detail?.appointmentAllowanceAdjustment),
          },
        ]}
      />

      <Section
        title="그룹수업 수당"
        onClick={() => openEachSalaryDetail("GROUP")}
        price={detail?.totalClassAllowance}
        submodules={[
          { id: 1, title: "수당", rightText: moneyFormatter(detail?.classAllowance) },
          { id: 2, title: "조정액", rightText: moneyFormatter(detail?.classAllowanceAdjustment) },
        ]}
      />

      <Section
        withRightArrow={false}
        title="급여 정산 추가 조정액"
        price={detail?.settlementAdjustment}
      />

      <Section
        withRightArrow={false}
        isLast={true}
        title="공제 내역"
        price={detail?.deductionAmount}
        submodules={[
          { id: 1, title: "공제율", rightText: `${detail?.deductionRate}%` },
          { id: 2, title: "조정액", rightText: moneyFormatter(detail?.deductionAmount) },
        ]}
      />

      {detail?.settlementStatus !== "PAYROLL_CONFIRMED" &&
        detail?.settlementStatus !== "PAYROLL_FORCED_CONFIRMED" && (
          <div className="bottom-buttons">
            <button className="red" onClick={() => handleOpenDrawer("objection")}>
              이의 제기
            </button>

            <button className="blue" onClick={() => handleOpenDrawer("agree")}>
              내역 동의
            </button>
          </div>
        )}

      <ContractDetailPop seqNewSettlementPayroll={seqNewSettlementPayroll} />
      <EachSalaryDetailPop
        salaryType={salaryType}
        seqNewSettlementPayroll={seqNewSettlementPayroll}
      />
      <AgreePopup
        seqNewSettlementPayroll={seqNewSettlementPayroll}
        lastRequestDt={detail?.lastRequestDt}
        closeAfterAction={closeAfterAction}
      />
      <ObjectionPopup
        seqNewSettlementPayroll={seqNewSettlementPayroll}
        closeAfterAction={closeAfterAction}
      />
    </div>
  );
};

const Overview = ({ onClick, detail }: any) => {
  return (
    <div className="overview">
      <div>
        {getPayrollStatus(detail.settlementStatus)}
        <span className="title">{detail.payrollName}</span>
      </div>
      <span className="price">{moneyFormatter(detail.totalPay)}</span>
      <span className="period">{`${
        changeDateFormat(detail.payStartDt, true) + " ~ " + changeDateFormat(detail.payEndDt, true)
      }`}</span>
      <button onClick={onClick}>
        <img src={ReceiptIcon} />
        계약 내역 보기
      </button>
      <span className="date">{`급여 내역 발송일 : ${changeDateFormat(
        detail?.lastRequestDt?.split("T")[0],
        true
      )} (${getDwk(new Date(detail?.lastRequestDt).getDay())})`}</span>
    </div>
  );
};

const Section = ({
  isLast = false,
  withRightArrow = true,
  onClick = () => {},
  title,
  price,
  submodules,
}: any) => {
  return (
    <div className={`section ${isLast && "last"}`}>
      <div onClick={onClick}>
        <span className="title">{title}</span>
        <div>
          <span className="price">{moneyFormatter(price)}</span>
          {withRightArrow && <img src={RightIcon} />}
        </div>
      </div>

      {submodules?.length && (
        <div className="submodules">
          {submodules.map((submodule: any, index: number) => {
            return (
              <div key={index}>
                <span className="title">{submodule.title}</span>
                <span className="right-text">{submodule.rightText}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SalaryDetails;
